/* assets/free-gift-picker.js */
(() => {
  'use strict';

  // Verhindert doppeltes Laden
  if (window.__FGP_LOADED__) return;
  window.__FGP_LOADED__ = true;

  /* =========================
   * Helpers
   * ========================= */
  const money = (cents) => {
    try { return (cents / 100).toFixed(2).replace('.', ',') + ' €'; }
    catch (e) { return cents; }
  };

  // Liest die JSON-Konfiguration aus dem Snippet (jeweils die nächste im Drawer, sonst global)
  const readConfig = (root = document) => {
    const el = root.querySelector('#free-gift-config') || document.querySelector('#free-gift-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); }
    catch (e) { return null; }
  };

  // Ermittelt die aktuell freigeschaltete Stufe anhand des Warenkorbwerts (in Cent)
  const eligibleTier = (cfg, totalCents) => {
    if (!cfg || !cfg.thresholds) return 0;
    let ok = 0;
    cfg.thresholds.forEach(t => {
      if (totalCents >= (t.amount * 100)) ok = Math.max(ok, t.tier);
    });
    return ok;
  };

  const getCart = async () =>
    (await fetch('/cart.js', { credentials: 'same-origin' })).json();

  const hasGift = (cart) =>
    (cart.items || []).some(i => (i.properties || {})._free_gift === 'true');

  // Sucht den nächstgelegenen Drawer (falls Event aus Mini-Cart kommt)
  const nearestDrawer = (from) => from?.closest?.('cart-drawer') || document;

  /* =========================
   * Rendering
   * ========================= */
  const renderRows = (container, cfg, eligible, selectedVariantId) => {
    container.innerHTML = '';
    (cfg.tiers || []).forEach(tier => {
      const disabled = tier.tier > eligible;

      const row = document.createElement('div');
      row.className = 'free-gift-row' + (disabled ? ' free-gift-row--disabled' : '');

      const head = document.createElement('div');
      head.className = 'free-gift-row__header';
      head.textContent = String(tier.label || '').trim();
      row.appendChild(head);

      const list = document.createElement('div');
      list.className = 'free-gift-row__list';

      (tier.products || []).forEach(p => {
        if (!p || !p.id || p.available === false) return;

        const card = document.createElement('div');
        card.className = 'free-gift-card';
        if (disabled) card.classList.add('free-gift-card--disabled');

        card.dataset.variantId = p.variant_id;
        card.dataset.tier = tier.tier;

        card.innerHTML = `
          <img src="${p.image || ''}" alt="">
          <div class="gift-meta">
            <div class="gift-title">${p.title || ''}</div>
            <div class="gift-prices">
              <span class="gift-compare">${money((p.compare_at_price || p.price))}</span>
              <span class="gift-free">0,00 €</span>
            </div>
          </div>
        `;

        if (selectedVariantId && String(selectedVariantId) === String(p.variant_id)) {
          card.classList.add('free-gift-card--selected');
        }

        list.appendChild(card);
      });

      row.appendChild(list);
      container.appendChild(row);
    });
  };

  /* =========================
   * Gift-Operationen
   * ========================= */
  const removeExistingGifts = async (cart) => {
    const removals = [];
    (cart.items || []).forEach(item => {
      if ((item.properties || {})._free_gift === 'true') {
        removals.push({ id: item.key, quantity: 0 });
      }
    });
    for (const r of removals) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(r)
        });
      } catch (e) {}
    }
  };

  // entfernt Geschenke, wenn Schwelle nicht mehr erreicht ist
  const cleanupInvalidGifts = async (cfg) => {
    if (!cfg) return;
    try {
      const cart = await getCart();
      const eligible = eligibleTier(cfg, cart.total_price);
      const removals = [];

      (cart.items || []).forEach(item => {
        const props = item.properties || {};
        const tier = parseInt(props._free_gift_tier || '0', 10) || 0;
        if (props._free_gift === 'true' && (eligible === 0 || tier > eligible)) {
          removals.push({ id: item.key, quantity: 0 });
        }
      });

      for (const r of removals) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(r)
          });
        } catch (e) {}
      }

      if (removals.length) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (e) {}
  };

  let claiming = false;
  const claimGift = async (variantId, tier) => {
    if (claiming) return;
    claiming = true;
    try {
      const cart = await getCart();
      await removeExistingGifts(cart);

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: { _free_gift: 'true', _free_gift_tier: String(tier) }
          }]
        })
      });

      closeModal();
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (e) {
      console.error('Gift add failed', e);
    } finally {
      claiming = false;
    }
  };

  /* =========================
   * Modal-Logik
   * ========================= */
  const openModal = async (contextEl) => {
    const root   = nearestDrawer(contextEl || document);
    const dialog = root.querySelector('#free-gift-dialog') || document.querySelector('#free-gift-dialog');
    const rows   = root.querySelector('.free-gift-rows') || document.querySelector('.free-gift-rows');
    const cfg    = readConfig(root);
    if (!dialog || !rows || !cfg) return;

    // Schneller First Paint mit der im Snippet gelieferten cartTotal
    try { renderRows(rows, cfg, eligibleTier(cfg, cfg.cartTotal), null); } catch (e) {}

    // Danach Live-Daten laden
    try {
      const cart = await getCart();
      const selected = (cart.items || []).find(i => (i.properties || {})._free_gift === 'true')?.variant_id || null;
      renderRows(rows, cfg, eligibleTier(cfg, cart.total_price), selected);
    } catch (e) {}

    if (!dialog.open) dialog.showModal();
  };

  const closeModal = () => {
    const dialog = document.querySelector('#free-gift-dialog');
    if (dialog && dialog.open) dialog.close();
  };

  /* =========================
   * Sichtbarkeit der Einstiegskachel
   * ========================= */
  const updateEntrypointVisibility = async () => {
    const entry = document.querySelector('.free-gift-entrypoint');
    if (!entry) return;
    try {
      const cart = await getCart();
      entry.style.display = hasGift(cart) ? 'none' : (cart.item_count > 0 ? '' : 'none');
    } catch (e) {
      entry.style.display = 'none';
    }
  };

  /* =========================
   * Event-Binding (delegiert, einmalig)
   * ========================= */
  const bind = () => {
    if (document.__FGP_BOUND__) return;
    document.__FGP_BOUND__ = true;

    document.addEventListener('click', (e) => {
      const openBtn = e.target.closest?.('[data-action="open-free-gift-modal"]');
      if (openBtn) { e.preventDefault(); openModal(openBtn); return; }

      const closeBtn = e.target.closest?.('.free-gift-close');
      if (closeBtn) { e.preventDefault(); closeModal(); return; }

      const card = e.target.closest?.('.free-gift-card');
      if (card && !card.classList.contains('free-gift-card--disabled')) {
        e.preventDefault();
        const vid  = card.dataset.variantId;
        const tier = parseInt(card.dataset.tier || '0', 10) || 0;
        if (vid) claimGift(vid, tier);
        return;
      }
    });

    document.addEventListener('keydown', (e) => {
      const trigger = e.target.closest?.('[data-action="open-free-gift-modal"]');
      if (trigger && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault(); openModal(trigger);
      }
    });
  };

  /* =========================
   * Bootstrapping
   * ========================= */
  const boot = async () => {
    bind();
    await updateEntrypointVisibility();
    const cfg = readConfig();
    if (cfg) cleanupInvalidGifts(cfg);
  };

  // Beim ersten Laden und bei Cart-Events (z.B. Add-to-Cart im Hintergrund)
  document.addEventListener('DOMContentLoaded', boot);
  document.documentElement.addEventListener('cart:updated', boot);
  document.documentElement.addEventListener('cart:refresh', boot);
})();
